import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbModal from '../security-toolbox-ui/StbModal';
import { useLabExecutionMethods, useSaveLabRow } from '../../hooks/lab-hooks';
import {
  LabExecutionMethod,
  labMethodBlockers,
  labSlugify,
  LAB_ACTION_TYPES,
  LAB_TABLES,
  LAB_TRANSPORTS,
} from '../../support/labService';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabBlockerList,
  LabCheckbox,
  LabDerivedKey,
  LabFieldRow,
  LabOptionSelect,
  labErrorMessage,
} from './LabShared';
import { LabSpecBuilder } from './LabSpecFields';

export type LabExecutionMethodEditorModalProps = {
  open: boolean;
  /** Null creates. An existing row keeps its key - items refer to it by id, authors by key. */
  method: LabExecutionMethod | null;
  onClose: () => void;
  onSaved?: (method: LabExecutionMethod) => void;
};

const EMPTY: Partial<LabExecutionMethod> = {
  key: '',
  name: '',
  description: '',
  action_type: 'command',
  transport: null,
  payload_template: {},
  expect_template: {},
  default_timeout_seconds: 300,
  supports_check: true,
  order: 0,
  enabled: true,
  metadata: {},
};

/**
 * Author one way of carrying work out.
 *
 * The field that makes this table safe is the action type. Everything the agent can do is one of
 * twelve actions it already implements, and a method is a *description* of work rather than a new
 * runner: terraform and ansible are `script` with a different interpreter, powershell is `command`
 * with a different shell, a REST call is `api_call`. Which is why adding a method here needs no
 * agent release and no pod redeploy - and why there is no free-text field for "the tool".
 *
 * The templates are merged *under* an item's own payload and expect, so what goes here is the part
 * that is true of every use of the method: the interpreter, the arguments that make it
 * non-interactive, the status codes that count. Anything specific to what is being configured
 * belongs on the item.
 */
const LabExecutionMethodEditorModal: React.FC<LabExecutionMethodEditorModalProps> = ({
  open,
  method,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabExecutionMethod>>(EMPTY);
  const [jsonErrors, setJsonErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const save = useSaveLabRow<Partial<LabExecutionMethod> & { id?: number }>(
    LAB_TABLES.executionMethod,
  );

  // The rest of the table, for the duplicate-key check. A clash matters more here than usual:
  // two methods with one key means an author picking from a list cannot tell them apart.
  const { data: catalogue } = useLabExecutionMethods({}, { enabled: open });
  const methods = useMemo(() => catalogue || [], [catalogue]);

  useEffect(() => {
    if (!open) return;

    setDraft(method ? { ...method } : { ...EMPTY });
    setJsonErrors({});
    setFormError(null);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, method?.id]);

  const set = (patch: Partial<LabExecutionMethod>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const generatedKey = useMemo(
    () => (method ? method.key : labSlugify(draft.name || '')),
    [method, draft.name],
  );

  const blockers = useMemo(
    () => labMethodBlockers({ ...(EMPTY as LabExecutionMethod), ...draft } as LabExecutionMethod),
    [draft],
  );

  const handleSubmit = async () => {
    if (!draft.name?.trim()) {
      setFormError('Give the method a name.');
      return;
    }

    if (!generatedKey) {
      setFormError('No key could be derived from that name. Use letters or digits somewhere in it.');
      return;
    }

    if (!draft.action_type) {
      setFormError(
        'Pick the action the agent is asked to perform. A method that does not resolve to one of these cannot be run at all.',
      );
      return;
    }

    const clash = methods.find(
      (candidate) => candidate.id !== draft.id && candidate.key === generatedKey,
    );

    if (clash) {
      setFormError(
        `"${clash.name}" already uses the key ${generatedKey}. Edit that one instead, or name this differently.`,
      );
      return;
    }

    const badJson = ['payload_template', 'expect_template', 'metadata'].find(
      (field) => jsonErrors[field],
    );

    if (badJson) {
      setFormError(`${badJson.replace('_', ' ')}: ${jsonErrors[badJson]}`);
      return;
    }

    setFormError(null);

    try {
      const saved = await save.mutateAsync({ ...draft, key: generatedKey });
      onSaved?.(saved as LabExecutionMethod);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);
  const itemCount = method?.item_count ?? 0;

  return (
    <StbModal
      open={open}
      role="update"
      title={method ? `Edit ${method.name}` : 'New execution method'}
      size="large"
      scrollable
      submitLabel={method ? 'Save method' : 'Create method'}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {itemCount > 0 && (
          <p className="m-0 text-sm text-amber-800">
            {itemCount === 1 ? 'One item uses this method' : `${itemCount} items use this method`}, in
            one or more of their four roles. Changes reach all of them the next time each scenario is
            compiled.
          </p>
        )}

        <TextField
          label="Name"
          value={draft.name || ''}
          onChange={(evt) => set({ name: evt.target.value })}
          helperText="What an author picks it out of a list by - REST API, Ansible playbook, PowerShell."
          fullWidth
        />

        <LabDerivedKey
          value={generatedKey}
          frozen={!!method}
          label="Method key"
          usage="Items and device types refer to the method by it."
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => set({ description: evt.target.value })}
          helperText="When to reach for this rather than another. One line is plenty."
          multiline
          minRows={2}
          fullWidth
        />

        <LabFieldRow>
          <LabOptionSelect
            label="Action the agent performs"
            value={draft.action_type}
            options={LAB_ACTION_TYPES}
            onChange={(action_type) => set({ action_type })}
            helpText="One of what the agent already implements. This is what makes a new method safe: nothing is deployed into a pod to support it."
          />
          <LabOptionSelect
            label="Transport"
            value={draft.transport}
            options={LAB_TRANSPORTS}
            onChange={(transport) => set({ transport: (transport || null) as any })}
            allowEmpty
            emptyLabel="Whatever the device uses"
            helpText="Set this only when the method needs a particular one. Leaving it empty takes the device's own, which is usually right."
          />
        </LabFieldRow>

        <LabFieldRow>
          <TextField
            label="Default timeout (seconds)"
            type="number"
            value={draft.default_timeout_seconds ?? 300}
            onChange={(evt) =>
              set({
                default_timeout_seconds: evt.target.value === '' ? 300 : Number(evt.target.value),
              })
            }
            helperText="Used when an item's spec names none. A REST call and a terraform apply cannot share one, which is why this lives here."
            fullWidth
          />
          <TextField
            label="Order"
            type="number"
            value={draft.order ?? 0}
            onChange={(evt) => set({ order: evt.target.value === '' ? 0 : Number(evt.target.value) })}
            helperText="Where it sits in the pick lists. Nothing else reads it."
            fullWidth
          />
        </LabFieldRow>

        <LabCheckbox
          label="Can observe state, not only change it"
          checked={draft.supports_check !== false}
          onChange={(supports_check) => set({ supports_check })}
          helpText="Clear this for a method that only applies - terraform apply, a file push. Used for validation, health or completion, such a method reports its own exit code, so the lab passes whether or not the pod is in the state the item claims. The compile reports it rather than refusing, because an author may have meant it."
        />

        {/* The one combination worth naming, because it passes every time and looks like a check. */}
        {draft.supports_check === false && (
          <p className="m-0 text-sm text-amber-800">
            Only honest as an action method. Anything scored against it is scoring the tool
            exiting zero.
          </p>
        )}

        <LabCheckbox
          label="Enabled"
          checked={draft.enabled !== false}
          onChange={(enabled) => set({ enabled })}
          helpText="Disabling retires a method without deleting it: items keep referring to it, and the compile generates nothing for the roles it fills. That is how a method is withdrawn without breaking the labs that named it."
        />

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.methodPayloadTemplate}
          value={draft.payload_template}
          resetKey={method?.id ?? 'new'}
          onChange={(payload_template, error) => {
            setJsonErrors((current) => ({ ...current, payload_template: error }));
            if (!error) set({ payload_template });
          }}
        />

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.methodExpectTemplate}
          value={draft.expect_template}
          resetKey={method?.id ?? 'new'}
          onChange={(expect_template, error) => {
            setJsonErrors((current) => ({ ...current, expect_template: error }));
            if (!error) set({ expect_template });
          }}
        />

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.methodMetadata}
          value={draft.metadata}
          resetKey={method?.id ?? 'new'}
          onChange={(metadata, error) => {
            setJsonErrors((current) => ({ ...current, metadata: error }));
            if (!error) set({ metadata });
          }}
        />

        <LabBlockerList
          blockers={blockers}
          heading="What this method will and will not honestly do:"
        />
      </div>
    </StbModal>
  );
};

export default LabExecutionMethodEditorModal;
